import { Modal, Button } from "antd";
import { ArrowLeftOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { useState } from "react";
import { updateProject } from "../utils/core";
import { useGlobalContext } from "@src/context/globalContext";
import LoaderOverlay from "./loaderOverlayTwo";

export const PageLayout = ({
  children,
  heading,
  domainData,
  store,
  open,
  setOpen,
  bulkCreatePropsArray,
  setBulkCreatePropsArray,
  previewOpen,
  setPreviewOpen,
  setProgressBarPercentage,
}: {
  children: React.ReactNode;
  heading: string;
  domainData: any;
  store: any;
  open: any;
  setOpen: any;
  bulkCreatePropsArray: any;
  setBulkCreatePropsArray: any;
  previewOpen: any;
  setPreviewOpen: any;
  setProgressBarPercentage: any;
}) => {
  const {
    UserDetail: [userDetails],
    ScriptDetails: [scriptDetails],
    PreviewLoader: [isPreviewLoading],
    BasicEditorSyncAfterCrud:[basicEditorSyncAfterCrud]
  } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const [tutorialKey, setTutorialKey] = useState(0);
  
  const showModal = () => {
    setIsModalOpen(true);
  };
  
  const showTutorialModal = () => {
    setTutorialKey(prev => prev + 1); // Force iframe to re-render
    setIsTutorialModalOpen(true);
  };
  
  const handleOk = async () => {
    await updateProject(userDetails, store, scriptDetails);
    const url = `https://${domainData?.domain_name}/`;
    window.location.href = url;
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  
  const handleTutorialCancel = () => {
    setIsTutorialModalOpen(false);
  };
  
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        margin: "0px",
        fontFamily: "var(--font-sans, 'Geist', 'DM Sans', system-ui, sans-serif)",
        backgroundColor: "var(--color-canvas)",
      }}
    >
      <LoaderOverlay isLoading={basicEditorSyncAfterCrud} />

      {/* Top bar — matches adgen-fe navbar style */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          height: "56px",
          minHeight: "56px",
          backgroundColor: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border)",
          paddingLeft: "24px",
          paddingRight: "24px",
          boxShadow: "0 1px 3px rgba(15, 15, 12, 0.04)",
          zIndex: 300,
        }}
      >
        {/* Left: back + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
          <button
            onClick={showModal}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              width: "32px",
              height: "32px",
              cursor: "pointer",
              color: "var(--color-ink-2)",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "var(--color-sidebar-row-hover)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
            }}
            aria-label="Go back"
          >
            <ArrowLeftOutlined style={{ fontSize: "14px" }} />
          </button>

          <img
            src={
              domainData?.premium_domain_name?.includes("quickads")
                ? "/quickads_logo.png"
                : domainData?.logo_url
            }
            width={
              domainData?.premium_domain_name?.includes("quickads") ? 110 : undefined
            }
            alt="Logo"
            style={{ maxHeight: "32px", cursor: "pointer", objectFit: "contain" }}
            onClick={showModal}
          />
        </div>

        {/* Centre: info notice — lime pill matching standalone topbar__notice */}
        <div style={{ display: "flex", alignItems: "center", flex: 1, justifyContent: "center", padding: "0 24px" }}>
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "5px 14px 5px 6px",
            background: "var(--color-primary-25)",
            border: "1px solid rgba(132, 204, 22, 0.28)",
            borderRadius: "var(--r-full)",
            color: "var(--color-primary-800)",
            fontSize: "12px",
            fontWeight: 500,
            maxWidth: "560px",
            overflow: "hidden",
            whiteSpace: "nowrap" as const,
            textOverflow: "ellipsis",
            lineHeight: 1.4,
          }}>
            <span style={{
              width: "20px",
              height: "20px",
              borderRadius: "999px",
              background: "var(--color-primary-500)",
              color: "var(--color-primary-on)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: "10px",
              fontWeight: 700,
            }}>✦</span>
            Lip syncing, watermark-free content, and transparent avatars are available in the final download
          </div>
        </div>

        {/* Right: tutorial button */}
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <Button
            onClick={showTutorialModal}
            style={{
              background: "transparent",
              borderColor: "var(--color-border)",
              color: "var(--color-ink-2)",
              fontWeight: 500,
              fontSize: "13px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
            icon={<PlayCircleOutlined />}
          >
            Tutorial
          </Button>
        </div>
      </div>
      {isPreviewLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "6px 24px",
            backgroundColor: "var(--color-surface)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <span style={{
            fontSize: "12px",
            color: "var(--color-ink-3)",
            fontWeight: 400,
          }}>
            Once rendering is completed, the download will start automatically and the preview will be re-enabled.
          </span>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
        }}
      >
        {children}
      </div>

      <Modal
        title="Go Back"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save and Go Back to Home"
        okButtonProps={{
          style: { backgroundColor: "var(--color-primary-500)" }, // Change the background and border color
        }}
      >
        <p>Are you sure you want to go back to the home page?</p>
      </Modal>
      
      <Modal
        title="Tutorial"
        open={isTutorialModalOpen}
        onCancel={handleTutorialCancel}
        onOk={handleTutorialCancel}
        footer={null}
        width={800}
        centered
        afterClose={() => {
          // Modal is closed, no need for manual cleanup since iframe will be unmounted
        }}
      >
        <div style={{ textAlign: "center" }}>
          <iframe
            key={tutorialKey}
            width="100%"
            height="450"
            src="https://www.youtube.com/embed/tng_SNt5HEI?si=B5ksb_e8Kh6gKDA2"
            title="Tutorial Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </Modal>
    </div>
  );
};
